import {
  QueryResultSchema,
  SemanticModelSchema,
  type QueryRequest,
  type QueryResult,
  type SemanticModel,
} from '@aibi/contracts';
import { ok, type AppError, type Result } from '@aibi/shared';
import { z } from 'zod';
import { postJson, requestJson, type FetchFn } from '../../api/http-client';

export const QUERY_API_ROUTES = {
  models: '/api/query/models',
  execute: '/api/query/execute',
} as const;

const QueryModelsResponseSchema = z.strictObject({
  models: z.array(SemanticModelSchema).max(100),
});

/**
 * Cliente de la ejecucion real del servidor. El navegador solo envia `QueryRequest`
 * (Analysis State + id del modelo): nunca SQL, rutas ni credenciales de fuentes.
 */
export interface QueryApi {
  models(signal?: AbortSignal): Promise<Result<readonly SemanticModel[], AppError>>;
  execute(request: QueryRequest, signal?: AbortSignal): Promise<Result<QueryResult, AppError>>;
}

export interface QueryApiOptions {
  readonly baseUrl: string;
  readonly fetch: FetchFn;
}

/**
 * @example
 * const api = createQueryApi({ baseUrl: '', fetch: window.fetch.bind(window) });
 * const models = await api.models();
 */
export function createQueryApi({ baseUrl, fetch }: QueryApiOptions): QueryApi {
  return {
    models: async (signal) => {
      const response = await requestJson(
        fetch,
        `${baseUrl}${QUERY_API_ROUTES.models}`,
        QueryModelsResponseSchema,
        signal ? { signal } : {},
      );
      return response.ok ? ok(response.value.models) : response;
    },
    execute: (request, signal) =>
      postJson(
        fetch,
        `${baseUrl}${QUERY_API_ROUTES.execute}`,
        request,
        QueryResultSchema,
        signal ? { signal } : {},
      ),
  };
}
